import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { reportPdfStyles as styles } from '../../../Styles/reportPdfStyles';

interface SalesReportData {
  dateRange: string;
  kpis: {
    totalRevenue: number;
    totalOrders: number;
    averageOrderValue: number;
    topProductsCount: number;
  };
  revenueTrends?: Array<{ date: string; revenue: number; orders: number; }>;
  ordersByStatus?: Array<{ status: string; count: number; revenue: number; }>;
  topProducts?: Array<{ productName: string; quantitySold: number; totalRevenue: number; }>;
  paymentMethods?: Array<{ method: string; count: number; revenue: number; }>;
  topClients?: Array<{ clientName: string; totalRevenue: number; orderCount: number; }>;
}

interface SalesPDFReportProps {
  data: SalesReportData;
}

const SalesPDFReport: React.FC<SalesPDFReportProps> = ({ data }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ro-RO', {
      style: 'currency',
      currency: 'RON',
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('ro-RO').format(value);
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Sales Report</Text>
          <Text style={styles.subtitle}>Cafe-Mag CRM</Text>
          <Text style={styles.dateRange}>Period: {data.dateRange}</Text>
        </View>

        {/* KPIs */}
        <View style={styles.kpiSection}>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Total Revenue</Text>
            <Text style={styles.kpiValue}>{formatCurrency(data.kpis.totalRevenue)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Total Orders</Text>
            <Text style={styles.kpiValue}>{formatNumber(data.kpis.totalOrders)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Average Order Value</Text>
            <Text style={styles.kpiValue}>{formatCurrency(data.kpis.averageOrderValue)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Top Products</Text>
            <Text style={styles.kpiValue}>{formatNumber(data.kpis.topProductsCount)}</Text>
          </View>
        </View>

        {/* Revenue Trends */}
        {data.revenueTrends && data.revenueTrends.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Revenue Trends</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Date</Text>
                <Text style={styles.tableCellHeader}>Revenue</Text>
                <Text style={styles.tableCellHeader}>Orders</Text>
              </View>
              {data.revenueTrends.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.date}</Text>
                  <Text style={styles.tableCell}>{formatCurrency(item.revenue)}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.orders)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Orders by Status */}
        {data.ordersByStatus && data.ordersByStatus.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Orders by Status</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Status</Text>
                <Text style={styles.tableCellHeader}>Count</Text>
                <Text style={styles.tableCellHeader}>Revenue</Text>
              </View>
              {data.ordersByStatus.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.status}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.count)}</Text>
                  <Text style={styles.tableCell}>{formatCurrency(item.revenue)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Top Products */}
        {data.topProducts && data.topProducts.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Top Products</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Product</Text>
                <Text style={styles.tableCellHeader}>Quantity Sold</Text>
                <Text style={styles.tableCellHeader}>Revenue</Text>
              </View>
              {data.topProducts.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.productName}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.quantitySold)}</Text>
                  <Text style={styles.tableCell}>{formatCurrency(item.totalRevenue)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Payment Methods */}
        {data.paymentMethods && data.paymentMethods.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Payment Methods</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Method</Text>
                <Text style={styles.tableCellHeader}>Count</Text>
                <Text style={styles.tableCellHeader}>Revenue</Text>
              </View>
              {data.paymentMethods.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.method}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.count)}</Text>
                  <Text style={styles.tableCell}>{formatCurrency(item.revenue)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Top Clients */}
        {data.topClients && data.topClients.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Top Clients</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Client</Text>
                <Text style={styles.tableCellHeader}>Revenue</Text>
                <Text style={styles.tableCellHeader}>Orders</Text>
              </View>
              {data.topClients.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.clientName}</Text>
                  <Text style={styles.tableCell}>{formatCurrency(item.totalRevenue)}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.orderCount)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Footer */}
        <Text style={styles.footer} fixed>
          Generated by Cafe-Mag CRM - {new Date().toLocaleDateString('ro-RO')}
        </Text>
      </Page>
    </Document>
  );
};

export default SalesPDFReport;
